import { defineStore } from "pinia";

interface ProductCustomization {
  // Style de l'image
  removeBackground: boolean;
  backgroundColor: string;

  // Style du prix
  showPrice: boolean;
  priceScale: number;
  priceSize: "price-sm" | "price-md" | "price-lg";
  priceTTCSize: "price-ttc-sm" | "price-ttc-md" | "price-ttc-lg";

  // Style du pictogramme
  pictogram: {
    id: number;
    name: string;
    image_name: string;
  } | null;
  pictoScale: number;

  // Style de la marque
  brand: {
    id: number;
    name: string;
    image_name: string;
  } | null;
  brandScale: number;

  // Style de l'en-tête
  header: {
    id: number;
    name: string;
    image_name: string;
  } | null;

  // Style du texte
  titleScale: number;

  // Position et dimensions
  position: {
    x: number;
    y: number;
    width: number;
    height: number;
  };

  // Rotation
  rotation: number;

  // Z-index
  zIndex: number;
}

interface CustomizationState {
  customizations: {
    [key: string]: ProductCustomization;
  };
  blockIds: {
    [productId: number]: string; // Map des IDs de produit vers les IDs de bloc
  };
}

export const useProductCustomizationStore = defineStore(
  "productCustomization",
  {
    state: (): CustomizationState => ({
      customizations: {},
      blockIds: {},
    }),

    actions: {
      // Générer un ID de bloc unique
      generateBlockId(productId: number): string {
        if (!this.blockIds[productId]) {
          this.blockIds[productId] = `block-${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`;
        }
        return this.blockIds[productId];
      },

      setCustomization(
        productId: number,
        customization: Partial<ProductCustomization>
      ) {
        const blockId = this.generateBlockId(productId);
        this.customizations[blockId] = {
          ...this.getDefaultCustomization(),
          ...this.customizations[blockId],
          ...customization,
        };
      },

      getCustomization(productId: number): ProductCustomization {
        const blockId = this.blockIds[productId];
        if (!blockId) {
          return this.getDefaultCustomization();
        }
        return this.customizations[blockId] || this.getDefaultCustomization();
      },

      getDefaultCustomization(): ProductCustomization {
        return {
          removeBackground: false,
          backgroundColor: "#fff",
          showPrice: true,
          priceScale: 1,
          priceSize: "price-md",
          priceTTCSize: "price-ttc-md",
          pictogram: null,
          pictoScale: 1,
          brand: null,
          brandScale: 1,
          header: null,
          titleScale: 1,
          position: {
            x: 0,
            y: 0,
            width: 0,
            height: 0,
          },
          rotation: 0,
          zIndex: 0,
        };
      },

      resetCustomization(productId: number) {
        const blockId = this.blockIds[productId];
        if (blockId) {
          delete this.customizations[blockId];
          delete this.blockIds[productId];
        }
      },

      // Nouvelle méthode pour obtenir les paramètres de style pour un élément
      getElementSettings(productId: number): any {
        const customization = this.getCustomization(productId);
        return {
          removeBackground: customization.removeBackground,
          backgroundColor: customization.backgroundColor,
          showPrice: customization.showPrice,
          priceScale: customization.priceScale,
          priceSize: customization.priceSize,
          priceTTCSize: customization.priceTTCSize,
          pictogram: customization.pictogram,
          pictoScale: customization.pictoScale,
          brand: customization.brand,
          brandScale: customization.brandScale,
          header: customization.header,
          titleScale: customization.titleScale,
          position: customization.position,
          rotation: customization.rotation,
          zIndex: customization.zIndex,
        };
      },
    },
  }
);
